/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  SignalSlashIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { MonitorAgent } from "@/api/monitor";
import { useMonitorAgent } from "@/hooks/useMonitorAgent";
import { useServerDetail } from "@/hooks/useServerDetail";
import { MonitorBandwidthTab } from "@/components/monitor/tabs/MonitorBandwidthTab";
import { MonitorSystemTab } from "@/components/monitor/tabs/MonitorSystemTab";
import { computeAlerts } from "./alerts";
import { PowerControls, PowerTransition } from "./PowerControls";
import { useAgentOnline } from "./ServerCard";
import { StatusDot, fmtDuration } from "./kit";
import {
  CpuPanel,
  DiskIoPanel,
  DockerPanel,
  FilesystemPanel,
  GuestsPanel,
  MemoryPanel,
  NetworkPanel,
  PressurePanel,
  ProcessesPanel,
  ServicesPanel,
  SmartPanel,
  ThermalPanel,
  UpsPanel,
  ZfsPanel,
} from "./panels";

type View = "live" | "traffic" | "hardware";

interface Transition {
  kind: PowerTransition;
  at: number;
  sawOffline: boolean;
}

interface ServerDetailViewProps {
  agent: MonitorAgent;
  onEdit: () => void;
  onDelete: () => void;
}

const TRANSITION_TIMEOUT_MS = 15 * 60 * 1000;

export const ServerDetailView: React.FC<ServerDetailViewProps> = ({ agent, onEdit, onDelete }) => {
  const [view, setView] = useState<View>("live");
  const [transition, setTransition] = useState<Transition | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const { status, hardwareStats } = useMonitorAgent({ agent, includeHardwareStats: true });
  const { detail, isError, error, rates, history, dataUpdatedAt } = useServerDetail(agent);
  const statusOnline = useAgentOnline(status);

  // "Offline" = the detail poll is failing (the tunnel/agent is gone), not merely stale.
  const offline = agent.enabled && isError;
  const online = agent.enabled && !isError && !!detail;

  // Drive the transition banner (reboot / shutdown / wake) from the poll results.
  useEffect(() => {
    setTransition(null);
  }, [agent.id]);
  useEffect(() => {
    if (!transition) return;
    if (now - transition.at > TRANSITION_TIMEOUT_MS) {
      setTransition(null);
      return;
    }
    if (offline && !transition.sawOffline) {
      if (transition.kind === "shutdown") setTransition(null); // done: it is down
      else setTransition({ ...transition, sawOffline: true });
    } else if (online && transition.kind === "wake") {
      setTransition(null);
    } else if (online && transition.kind === "reboot" && transition.sawOffline) {
      setTransition(null);
    }
  }, [offline, online, transition, now, dataUpdatedAt]);
  useEffect(() => {
    if (!transition) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [transition]);

  const alerts = useMemo(() => (detail ? computeAlerts(detail, hardwareStats) : []), [detail, hardwareStats]);
  const crit = alerts.filter((a) => a.level === "crit").length;
  const warn = alerts.filter((a) => a.level === "warn").length;

  const state: "up" | "warn" | "down" | "idle" = !agent.enabled
    ? "idle"
    : offline
      ? "down"
      : crit > 0
        ? "down"
        : warn > 0
          ? "warn"
          : online
            ? "up"
            : "idle";

  return (
    <div className="sv">
      <section className="sv-hero">
        <div className="sv-hero-id">
          <div className="sv-hero-name">
            <StatusDot state={state} />
            {agent.name}
          </div>
          <div className="sv-hero-meta">
            {detail ? (
              <>
                <span>
                  <b>{detail.host.hostname}</b>
                </span>
                <span>
                  {detail.host.platform} {detail.host.platform_version}
                </span>
                <span>kernel {detail.host.kernel}</span>
                <span>{detail.host.arch}</span>
                {detail.host.virtualization && <span>vm: {detail.host.virtualization}</span>}
                <span>
                  up <b>{fmtDuration(detail.host.uptime_seconds)}</b>
                </span>
              </>
            ) : (
              <span>{agent.enabled ? (offline ? "no live data" : "connecting…") : "monitoring disabled"}</span>
            )}
          </div>
        </div>
        <div className="sv-hero-actions">
          <PowerControls
            agent={agent}
            online={online}
            onTransition={(kind) => setTransition({ kind, at: Date.now(), sawOffline: false })}
          />
          <button className="gh-btn" onClick={onEdit} title="Edit server">
            <PencilIcon /> Edit
          </button>
          <button className="gh-btn gh-btn--danger" onClick={onDelete} title="Remove server">
            <TrashIcon /> Remove
          </button>
        </div>
      </section>

      {transition && (
        <div className="sv-banner sv-banner--warn">
          <ExclamationTriangleIcon className="sv-pulse" />
          <span className="sv-banner-grow">
            {transition.kind === "reboot" && "Rebooting - waiting for the server to come back."}
            {transition.kind === "shutdown" && "Shutting down..."}
            {transition.kind === "wake" && "Magic packet sent - waiting for the server to boot."}
          </span>
          <span>{fmtDuration((now - transition.at) / 1000)}</span>
        </div>
      )}

      {offline && !transition && (
        <div className="sv-banner sv-banner--crit">
          <SignalSlashIcon />
          <span className="sv-banner-grow">
            Server is offline or the agent is unreachable
            {statusOnline ? " (live stream still connected)" : ""}.{" "}
            <span className="sv-mute">{error?.message}</span>
          </span>
        </div>
      )}

      {agent.enabled && (
        <div className="sv-tabs" role="tablist">
          {(
            [
              ["live", "Live"],
              ["traffic", "Traffic history"],
              ["hardware", "Hardware"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              role="tab"
              aria-selected={view === id}
              className={`gh-btn${view === id ? " gh-btn--on" : ""}`}
              onClick={() => setView(id)}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {view === "live" && detail && (
        <div style={offline ? { opacity: 0.45 } : undefined}>
          {alerts.length > 0 ? (
            <div className="sv-alerts" style={{ marginBottom: 12 }}>
              {alerts.map((a, i) => (
                <div className={`sv-alert sv-alert--${a.level}`} key={i}>
                  <span
                    className={`gh-dot ${a.level === "crit" ? "gh-dot--down" : a.level === "warn" ? "gh-dot--warn" : "gh-dot--idle"}`}
                    style={{ marginTop: 6 }}
                  />
                  <div>
                    <div className="sv-alert-t">{a.title}</div>
                    {a.detail && <div className="sv-alert-d">{a.detail}</div>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            !offline && (
              <div className="sv-banner sv-banner--ok" style={{ marginBottom: 12 }}>
                <CheckCircleIcon />
                <span>All checks green - no problems detected.</span>
              </div>
            )
          )}

          <div className="sv-board">
            <CpuPanel d={detail} hw={hardwareStats} history={history} />
            <MemoryPanel hw={hardwareStats} />
            <NetworkPanel d={detail} rates={rates} history={history} />
            <PressurePanel d={detail} />
            <GuestsPanel d={detail} />
            <DockerPanel d={detail} />
            <DiskIoPanel d={detail} rates={rates} history={history} />
            <FilesystemPanel hw={hardwareStats} />
            <UpsPanel d={detail} />
            <ZfsPanel d={detail} />
            <ThermalPanel hw={hardwareStats} d={detail} />
            <SmartPanel d={detail} />
            <ProcessesPanel d={detail} />
            <ServicesPanel d={detail} />
          </div>
        </div>
      )}

      {view === "live" && !detail && agent.enabled && !offline && <div className="sv-loading">collecting metrics…</div>}
      {view === "live" && !agent.enabled && <div className="sv-loading">Monitoring is disabled for this server. Enable it via Edit.</div>}

      {view === "traffic" && agent.enabled && <MonitorBandwidthTab agent={agent} />}
      {view === "hardware" && agent.enabled && <MonitorSystemTab agent={agent} />}
    </div>
  );
};
